///
/// EarthBrowser
///
/// Created by Matt Giger on 6/1/2024
/// Copyright (c) 2024 Geodata Labs
///

import { type cube } from "@stovokor/math/cube"
import { trig } from "@stovokor/math/math"
import { type sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { cellToVertexes, getRes0Cells, vertexToLatLng } from "h3-js"

export class HexSphereGeometry implements Geometry {
	public position: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Lines
	public bounds: cube
	public boundingSphere: sphere
	public transparency = false
	public available: boolean = true
	private undeletable: boolean = false

	constructor(context: StovokorContext, undeletable: boolean = false) {
		this.position = new VertexAttribute(context, 3)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position])
		this.undeletable = undeletable

		const rad = trig.rad
		let idx = 0
		const cells = getRes0Cells()
		cells.forEach(cellId => {
			const vertexIds = cellToVertexes(cellId).filter(v => v != '0')

			vertexIds.forEach(v => {
				const coord = vertexToLatLng(v)
				const lat = coord[0] * rad
				const lon = coord[1] * rad
				const coslat = Math.cos(lat)
				this.position.addPos(
					Math.sin(lon) * coslat,
					Math.sin(lat),
					Math.cos(lon) * coslat
				)
			})

			for(let i = 0; i < vertexIds.length; i++) {
				this.indexBuffer.index(idx + i)
				this.indexBuffer.index(idx + (i+1) % vertexIds.length)
			}
			idx += vertexIds.length
		})
		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		if(this.undeletable) {
			return
		}

		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}